import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { UploadCloud, FileSpreadsheet, X, CheckCircle2, AlertTriangle, Loader2, Building2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorText } from "@/lib/api";
import { MONTHS } from "@/lib/format";
import { toast } from "sonner";

export const BulkUploadDialog = ({ open, onOpenChange, onDone }) => {
  const [files, setFiles] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear() + 1);
  const [properties, setProperties] = useState([]);
  const [hotel, setHotel] = useState("__auto");
  useEffect(() => { if (open) api.get("/properties").then((r) => setProperties(r.data.filter((p) => p.is_active))).catch(() => {}); }, [open]);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (!job || job.done) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/bulk-jobs/${job.id}`);
        setJob(data);
        if (data.done) { toast.success("Bulk upload finished"); onDone(); }
      } catch (e) { /* keep polling */ }
    }, 2500);
    return () => clearInterval(t);
  }, [job, onDone]);

  const addFiles = (list) => {
    const arr = Array.from(list).filter((f) => /\.(xlsx|xls|xlsm|csv)$/i.test(f.name));
    setFiles((prev) => [...prev, ...arr.filter((f) => !prev.some((p) => p.name === f.name))]);
  };

  const start = async () => {
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("budget_year", String(year));
    if (hotel !== "__auto") fd.append("hotel_name", hotel);
    try {
      const { data } = await api.post("/projects/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setJob(data);
      setFiles([]);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  const reset = () => { setJob(null); setFiles([]); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Bulk Property Upload</DialogTitle>
          <DialogDescription>Drop the monthly P&amp;L files for several properties at once. Pick a registered property, or let the tool match each file to a registered property from the file name / report header. One budget project is created per property.</DialogDescription>
        </DialogHeader>
        {!job ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Budget Year</Label><Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="bulk-budget-year-input" className="font-mono" /></div>
              <div className="space-y-1.5">
                <Label>Property</Label>
                <select value={hotel} onChange={(e) => setHotel(e.target.value)} data-testid="bulk-property-select" className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                  <option value="__auto">Auto-detect per file (registered properties only)</option>
                  {properties.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }} onClick={() => inputRef.current?.click()}
              className="cursor-pointer rounded-lg border-2 border-dashed border-slate-300 hover:border-emerald-400 p-8 text-center" data-testid="bulk-drop-zone">
              <input ref={inputRef} type="file" multiple accept=".xlsx,.xls,.xlsm,.csv" className="hidden" data-testid="bulk-file-input" onChange={(e) => addFiles(e.target.files)} />
              <UploadCloud className="mx-auto text-emerald-600" size={30} />
              <p className="text-sm text-slate-700 mt-3 font-medium">Drop files for all properties here</p>
              <p className="text-xs text-slate-500 mt-1">e.g. "Profit and Loss Report Aug 2026 - Villa A.xls", "... - Villa B.xls"</p>
            </div>
            {files.length > 0 && (
              <ul className="max-h-40 overflow-auto space-y-1.5" data-testid="bulk-selected-files">
                {files.map((f) => (
                  <li key={f.name} className="flex items-center gap-2 text-sm"><FileSpreadsheet size={14} className="text-emerald-600" /><span className="truncate flex-1">{f.name}</span>
                    <button onClick={() => setFiles(files.filter((x) => x !== f))} className="text-slate-400 hover:text-red-600"><X size={14} /></button></li>
                ))}
              </ul>
            )}
            <Button onClick={start} disabled={!files.length || busy} className="w-full bg-slate-900 hover:bg-emerald-700" data-testid="bulk-start-button">
              {busy ? "Uploading..." : `Create projects from ${files.length} file${files.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="bulk-job-status">
            <p className="text-xs text-slate-500">{job.done ? "All properties processed." : "Processing — AI is reading each property's files. This takes ~30–60s per property."}</p>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
              {job.properties.map((p) => (
                <li key={p.hotel_name} className="flex items-center gap-3 px-4 py-3" data-testid={`bulk-property-${p.hotel_name.replace(/\s+/g, "-").toLowerCase()}`}>
                  {p.status === "done" ? <CheckCircle2 size={18} className="text-emerald-600" /> : p.status === "error" ? <AlertTriangle size={18} className="text-red-600" /> : p.status === "processing" ? <Loader2 size={18} className="text-amber-600 animate-spin" /> : <Building2 size={18} className="text-slate-400" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{p.hotel_name}</p>
                    <p className="text-[11px] text-slate-500 truncate">{p.files.join(", ")}{p.last_actual_month ? ` · actuals through ${MONTHS[p.last_actual_month - 1]}` : ""}</p>
                    {p.error && <p className="text-[11px] text-red-600">{p.error}</p>}
                  </div>
                  {p.project_id && <Link to={`/projects/${p.project_id}`} className="text-xs font-semibold text-emerald-700 hover:underline" data-testid={`bulk-open-${p.project_id}`}>Open</Link>}
                </li>
              ))}
            </ul>
            {job.done && <Button variant="outline" className="w-full" onClick={() => { reset(); onOpenChange(false); }} data-testid="bulk-close-button">Close</Button>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
